import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Grid3X3, AlertTriangle, RefreshCw } from 'lucide-react';
import { debug } from '../../utils/debug';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class MultiGridErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('MultiGrid Error Boundary caught an error:', error, errorInfo);
    
    // Log specific details about multi-grid related errors
    if (error.message.includes('includes') || error.message.includes('activeGridIds')) {
      console.error('Multi-grid state corruption detected:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      });
    }

    this.setState({ errorInfo });
    
    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = () => {
    // Reset error state to retry rendering
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    
    // Force a page reload as a last resort for multi-grid errors
    if (this.state.error?.message.includes('includes') || this.state.error?.message.includes('activeGridIds')) {
      debug('Forcing page reload due to multi-grid state corruption...');
      window.location.reload();
    }
  };

  public render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI for multi-grid errors
      return (
        <div className="min-h-[400px] bg-white rounded-lg shadow-sm border flex items-center justify-center">
          <div className="text-center p-8 max-w-md">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-danger-bg rounded-full">
                <AlertTriangle className="h-8 w-8 text-danger-fg" />
              </div>
            </div>
            
            <h3 className="text-lg font-semibold text-ink-900 mb-2">
              Multi-Grid Error
            </h3>
            
            <p className="text-ink-500 mb-4">
              {this.state.error?.message.includes('includes') || this.state.error?.message.includes('activeGridIds')
                ? 'A grid state error occurred. This is usually temporary and can be fixed by refreshing.'
                : 'An unexpected error occurred in the multi-grid system.'
              }
            </p>

            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-navy-500 text-white rounded hover:bg-navy-600 transition-colors"
              >
                <RefreshCw size={16} />
                Try Again
              </button>
              
              <button
                onClick={() => window.location.href = '/grid/multi-editor'}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-ink-300 text-ink-700 rounded hover:bg-ink-50 transition-colors"
              >
                <Grid3X3 size={16} />
                Return to Grid Home
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left">
                <summary className="text-sm text-ink-500 cursor-pointer hover:text-ink-700">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 text-xs bg-ink-100 p-3 rounded overflow-auto text-danger-fg">
                  {this.state.error.stack}
                </pre>
                {this.state.errorInfo && (
                  <pre className="mt-2 text-xs bg-ink-100 p-3 rounded overflow-auto text-ink-500">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component wrapper for easier usage
export function withMultiGridErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WrappedComponent(props: P) {
    return (
      <MultiGridErrorBoundary fallback={fallback}>
        <Component {...props} />
      </MultiGridErrorBoundary>
    );
  };
}